import { createClient } from '@supabase/supabase-js'

// Hybrid authentication system: 
// - Production: Uses Cloudflare Pages Functions (server-side)
// - Development: Uses direct Supabase calls (client-side)

let supabase = null;
const isDevelopment = (
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV === true)
    || window.location.port === '5173'
    || window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1'
);

if (isDevelopment) {
    // Development: Use client-side Supabase
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (supabaseUrl && supabaseKey) {
        try {
            supabase = createClient(supabaseUrl, supabaseKey);
            console.log('Development: Supabase initialized for client-side auth');
        } catch (error) {
            console.error('Failed to initialize Supabase:', error);
        }
    } else {
        console.warn('Development: Supabase environment variables not found');
    }
}

// Password validation requirements
const passwordRequirements = {
    length: { regex: /.{8,}/, element: 'req-length' },
    uppercase: { regex: /[A-Z]/, element: 'req-uppercase' },
    lowercase: { regex: /[a-z]/, element: 'req-lowercase' },
    number: { regex: /\d/, element: 'req-number' },
    special: { regex: /[!@#$%^&*(),.?":{}|<>]/, element: 'req-special' }
};

// Email validation
function validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

// Password validation
function validatePassword(password) {
    const results = {};
    let allValid = true;

    for (const [requirement, config] of Object.entries(passwordRequirements)) {
        const isValid = config.regex.test(password);
        results[requirement] = isValid;
        
        const element = document.getElementById(config.element);
        if (element) {
            element.classList.toggle('valid', isValid);
        }
        
        if (!isValid) allValid = false;
    }

    return { isValid: allValid, requirements: results };
}

// Show validation message
function showValidation(elementId, message, type) {
    const element = document.getElementById(elementId);
    if (element) {
        element.textContent = message;
        element.className = `validation-message ${type}`;
    }
}

// Clear validation message
function clearValidation(elementId) {
    const element = document.getElementById(elementId);
    if (element) {
        element.textContent = '';
        element.className = 'validation-message';
    }
}

// Set input validation state
function setInputState(inputId, state) {
    const input = document.getElementById(inputId);
    if (input) {
        input.classList.remove('valid', 'invalid', 'checking');
        if (state) {
            input.classList.add(state);
        }
    }
}

// Set button loading state
function setButtonLoading(loading) {
    const button = document.getElementById('signup-btn');
    const btnText = button.querySelector('.btn-text');
    const btnLoading = button.querySelector('.btn-loading');
    
    button.disabled = loading;
    if (loading) {
        button.classList.add('loading');
        btnText.style.display = 'none';
        btnLoading.style.display = 'inline';
    } else {
        button.classList.remove('loading');
        btnText.style.display = 'inline';
        btnLoading.style.display = 'none';
    }
}

// Handle signup
async function handleSignup(email, password) {
    setButtonLoading(true);

    try {
        if (isDevelopment && supabase) {
            // Development: Use client-side Supabase
            await handleDevelopmentSignup(email, password);
        } else {
            // Production: Use server-side functions
            await handleProductionSignup(email, password);
        }

    } catch (error) {
        console.error('Signup error:', error);
        
        // Handle specific error cases
        if (error.message.includes('already registered')) {
            showValidation('email-validation', 'This email is already registered. Try signing in instead.', 'error');
        } else if (error.message.includes('Invalid email')) {
            showValidation('email-validation', 'Please enter a valid email address.', 'error');
        } else if (error.message.includes('Password')) {
            showValidation('password-validation', error.message, 'error');
        } else {
            showValidation('email-validation', error.message || 'An error occurred during signup.', 'error');
        }
        
        setButtonLoading(false);
    }
}

// Development signup using client-side Supabase
async function handleDevelopmentSignup(email, password) {
    const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
            data: {
                profile_complete: false,
                email: email,
                display_name: null,
                first_name: null,
                last_name: null,
                full_name: null,
                handicap: null,
                email_verified: false,
                phone_verified: false
            }
        }
    });

    if (authError) {
        // Show validation message and throw error to re-enable button
        if (authError.message.includes('already registered')) {
            showValidation('email-validation', 'This email is already registered. Try signing in instead.', 'error');
        } else if (authError.message.includes('Invalid email')) {
            showValidation('email-validation', 'Please enter a valid email address.', 'error');
        } else if (authError.message.includes('Password')) {
            showValidation('password-validation', authError.message, 'error');
        } else {
            showValidation('email-validation', authError.message || 'An error occurred during signup.', 'error');
        }
        throw authError;
    }

    if (authData.user) {
        console.log('User created successfully:', authData.user.id);
        sessionStorage.setItem('completing_profile_user_id', authData.user.id);
        showValidation('email-validation', 'Account created! Redirecting to profile setup...', 'success');
        
        setTimeout(() => {
            window.location.href = './complete-profile.html';
        }, 2000);
    }
}

// Production signup using serverless functions
async function handleProductionSignup(email, password) {
    const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
    });

    const result = await response.json();

    if (!result.success) {
        // Show validation message and throw error to re-enable button
        if (result.error.includes('already registered')) {
            showValidation('email-validation', 'This email is already registered. Try signing in instead.', 'error');
        } else if (result.error.includes('Invalid email')) {
            showValidation('email-validation', 'Please enter a valid email address.', 'error');
        } else if (result.error.includes('Password')) {
            showValidation('password-validation', result.error, 'error');
        } else {
            showValidation('email-validation', result.error || 'An error occurred during signup.', 'error');
        }
        throw new Error(result.error);
    }

    if (result.user) {
        console.log('User created successfully:', result.user.id);
        
        // Store session data for profile completion
        localStorage.setItem('supabase_session', JSON.stringify({
            access_token: result.session.access_token,
            refresh_token: result.session.refresh_token,
            user: result.user
        }));
        
        sessionStorage.setItem('completing_profile_user_id', result.user.id);
        showValidation('email-validation', 'Account created! Redirecting to profile setup...', 'success');
        
        setTimeout(() => {
            window.location.href = './complete-profile.html';
        }, 2000);
    }
}

// DOM Ready
document.addEventListener('DOMContentLoaded', function() {
    const form = document.getElementById('signup-form');
    const emailInput = document.getElementById('email');
    const passwordInput = document.getElementById('password');
    const passwordToggle = document.getElementById('password-toggle');

    // Real-time email validation
    emailInput.addEventListener('input', function() {
        const email = this.value.trim();
        
        clearValidation('email-validation');
        
        if (email.length === 0) {
            setInputState('email', null);
            return;
        }

        if (validateEmail(email)) {
            setInputState('email', 'valid');
            showValidation('email-validation', 'Valid email address', 'success');
        } else {
            setInputState('email', 'invalid');
            showValidation('email-validation', 'Please enter a valid email address', 'error');
        }
    });

    // Real-time password validation
    passwordInput.addEventListener('input', function() {
        const password = this.value;
        
        clearValidation('password-validation');
        
        if (password.length === 0) {
            setInputState('password', null);
            // Reset all requirement indicators when password is empty
            Object.values(passwordRequirements).forEach(config => {
                const element = document.getElementById(config.element);
                if (element) {
                    element.classList.remove('valid');
                }
            });
            return;
        }

        const validation = validatePassword(password);
        
        if (validation.isValid) {
            setInputState('password', 'valid');
            showValidation('password-validation', 'Password meets all requirements', 'success');
        } else {
            setInputState('password', 'invalid');
            
            const missingReqs = Object.entries(validation.requirements)
                .filter(([req, valid]) => !valid)
                .map(([req]) => req)
                .join(', ');
            
            showValidation('password-validation', `Missing: ${missingReqs}`, 'error');
        }
    });

    // Password visibility toggle
    passwordToggle.addEventListener('click', function() {
        const isPassword = passwordInput.type === 'password';
        
        if (isPassword) {
            // Show password (visible)
            passwordInput.type = 'text';
            this.title = 'Hide password';
            this.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
                </svg>
            `;
        } else {
            // Hide password (hidden)
            passwordInput.type = 'password';
            this.title = 'Show password';
            this.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="m1 1 22 22" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="m9.9 4.24 1.6-.6a10 10 0 0 1 10.5 7.36" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="m14.12 14.12a3 3 0 0 1-4.24-4.24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="m1 12s2.86-6.4 7.5-8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="m8.6 15.8 3.4 1.2 3.4-1.2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            `;
        }
    });

    // Form submission
    form.addEventListener('submit', function(e) {
        e.preventDefault();
        
        const email = emailInput.value.trim();
        const password = passwordInput.value;

        // Clear previous validations
        clearValidation('email-validation');
        clearValidation('password-validation');

        // Validate all fields
        let isValid = true;

        if (!validateEmail(email)) {
            showValidation('email-validation', 'Please enter a valid email address', 'error');
            setInputState('email', 'invalid');
            isValid = false;
        }

        const passwordValidation = validatePassword(password);
        if (!passwordValidation.isValid) {
            showValidation('password-validation', 'Password does not meet requirements', 'error');
            setInputState('password', 'invalid');
            isValid = false;
        }

        if (isValid) {
            handleSignup(email, password);
        }
    });
}); 